import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './common.css';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PhonemeGames = ({ onNavigate }) => {
  const [selectedGame, setSelectedGame] = useState('');
  
  const games = [
    { id: 'letter-sorting', name: '1. Letter Sorting Game', icon: '🎯' },
    { id: 'mirror-match', name: '2. Mirror Match', icon: '🪞' },
    { id: 'trace-flip', name: '3. Trace and Flip', icon: '✍️' },
    { id: 'word-completion', name: '4. Word Completion', icon: '📝' },
    { id: 'picture-association', name: '5. Picture Association', icon: '🖼️' },
    { id: 'spot-difference', name: '6. Spot the Difference', icon: '🔍' }
  ];

  const handleGameChange = (e) => {
    setSelectedGame(e.target.value);
  };

  const renderGame = () => {
    switch(selectedGame) {
      case 'letter-sorting':
        return <LetterSortingGame />;
      case 'mirror-match':
        return <MirrorMatchGame />;
      case 'trace-flip':
        return <TraceFlipGame />;
      case 'word-completion':
        return <WordCompletionGame />;
      case 'picture-association':
        return <PictureAssociationGame />;
      case 'spot-difference':
        return <SpotDifferenceGame />;
      default:
        return <WelcomeScreen />;
    }
  };

  return (
    <div className="home-container">
      <Header onNavigate={onNavigate} showHomeButton={false} />
      <div className="home-container">
        <div className="hero-section">
          <h1 className="hero-title">🎮 Phoneme Confusion Games</h1>
          <p className="hero-description">
            Practice identifying and differentiating confusing letters: b, d, p, q
          </p>
        </div>

        <div style={{ 
          maxWidth: '600px', 
          margin: '30px auto', 
          textAlign: 'center' 
        }}>
          <label 
            htmlFor="game-selector" 
            style={{ 
              fontSize: '1.8rem', 
              fontWeight: 'bold', 
              color: '#2C3E50',
              display: 'block',
              marginBottom: '15px'
            }}
          >
            Choose a Game:
          </label>
          <select
            id="game-selector"
            className="form-select"
            value={selectedGame}
            onChange={handleGameChange}
            style={{
              width: '100%',
              fontSize: '1.5rem',
              padding: '15px 20px'
            }}
          >
            <option value="">-- Select a Game --</option>
            {games.map(game => (
              <option key={game.id} value={game.id}>
                {game.icon} {game.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginTop: '40px' }}>
          {renderGame()}
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Welcome Screen Component
const WelcomeScreen = () => (
  <div style={{
    background: 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
    borderRadius: '25px',
    padding: '60px 30px',
    textAlign: 'center',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)'
  }}>
    <h2 style={{ fontSize: '2.5rem', color: '#2C3E50', marginBottom: '20px' }}>
      Welcome to Phoneme Games! 🎉
    </h2>
    <p style={{ fontSize: '1.5rem', color: '#34495E', lineHeight: '2' }}>
      Select a game from the dropdown menu above to start practicing.
    </p>
  </div>
);

// 1. Letter Sorting Game Component
const LetterSortingGame = () => {
  const [buckets, setBuckets] = useState({ b: [], d: [], p: [], q: [] });
  const [availableLetters, setAvailableLetters] = useState([]);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [selectedLetter, setSelectedLetter] = useState(null);

  const resetGame = useCallback(() => {
    const letters = [];
    ['b', 'd', 'p', 'q'].forEach(letter => {
      for (let i = 0; i < 5; i++) {
        letters.push({ id: `${letter}-${i}`, letter });
      }
    });
    setAvailableLetters(shuffleArray(letters));
    setBuckets({ b: [], d: [], p: [], q: [] });
    setScore(0);
    setFeedback('');
    setSelectedLetter(null);
  }, []);

  useEffect(() => {
    resetGame();
  }, [resetGame]); // Wrapped resetGame in useCallback and reordered

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const handleDrop = (letter, bucketType) => {
    setAvailableLetters(prev => prev.filter(l => l.id !== letter.id));
    setBuckets(prev => ({
      ...prev,
      [bucketType]: [...prev[bucketType], letter]
    }));

    if (letter.letter === bucketType) {
      setScore(s => s + 1);
      setFeedback('✅ Correct!');
      playSound(letter.letter);
    } else {
      setFeedback(`❌ Oops! That was "${letter.letter}", not "${bucketType}"`);
    }
    setTimeout(() => setFeedback(''), 2000);
  };

  const handleLetterTap = (letter) => {
    setSelectedLetter(prev => prev?.id === letter.id ? null : letter);
  };

  const handleBucketTap = (bucketType) => {
    if (selectedLetter) {
      handleDrop(selectedLetter, bucketType);
      setSelectedLetter(null);
    }
  };

  const playSound = (letter) => {
    const utterance = new SpeechSynthesisUtterance(`The letter ${letter}`);
    utterance.lang = 'en-US';
    const voices = window.speechSynthesis.getVoices();
    const femaleVoice =
      voices.find(v => v.lang.startsWith('en') && /zira|samantha|victoria|karen|moira|fiona|veena|female|woman/i.test(v.name)) ||
      voices.find(v => v.lang.startsWith('en') && v.name.toLowerCase().includes('female'));
    if (femaleVoice) utterance.voice = femaleVoice;
    utterance.rate = 0.8;
    utterance.pitch = 4.0;
    utterance.volume = 1.0;
    window.speechSynthesis.speak(utterance);
  };

  return (
    <div className="quiz-card">
      <h2 className="section-title">🎯 Letter Sorting Game</h2>
      <p className="setup-description">
        Drag each letter into its correct bucket. Listen for audio feedback!
      </p>
      
      <div style={{ 
        textAlign: 'center', 
        fontSize: '2rem', 
        fontWeight: 'bold', 
        color: '#667eea',
        margin: '20px 0' 
      }}>
        Score: {score}
      </div>

      {feedback && (
        <div className={`feedback ${feedback.includes('✅') ? 'correct' : 'incorrect'}`}>
          {feedback}
        </div>
      )}

      {/* Buckets */}
      <div className="letter-sorting-grid">
        {['b', 'd', 'p', 'q'].map(bucketType => (
          <div
            key={bucketType}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const letterData = JSON.parse(e.dataTransfer.getData('letter'));
              handleDrop(letterData, bucketType);
            }}
            onClick={() => handleBucketTap(bucketType)}
            className={`letter-sorting-bucket ${selectedLetter ? 'letter-sorting-bucket-active' : ''}`}
          >
            <div style={{ 
              fontSize: '3rem', 
              fontWeight: 'bold', 
              marginBottom: '15px',
              color: '#2C3E50'
            }}>
              {bucketType}
            </div>
            <div style={{ fontSize: '1.2rem', color: '#7F8C8D', marginBottom: '10px' }}>
              {buckets[bucketType].length} letters
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', justifyContent: 'center' }}>
              {buckets[bucketType].map(l => (
                <span key={l.id} style={{ 
                  fontSize: '2rem', 
                  background: '#C8E6C9', 
                  padding: '5px 10px',
                  borderRadius: '8px'
                }}>
                  {l.letter}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Available Letters */}
      <div style={{ marginTop: '40px' }}>
        <h3 style={{ fontSize: '1.8rem', color: '#2C3E50', marginBottom: '15px' }}>
          Drag these letters:
        </h3>
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '15px', 
          justifyContent: 'center' 
        }}>
          {availableLetters.map(letter => (
            <div
              key={letter.id}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('letter', JSON.stringify(letter));
              }}
              onClick={() => handleLetterTap(letter)}
              className={`letter-tile ${selectedLetter?.id === letter.id ? 'letter-tile-selected' : ''}`}
              style={{ cursor: 'grab' }}
            >
              {letter.letter}
            </div>
          ))}
        </div>
      </div>

      <button 
        className="start-btn" 
        onClick={resetGame}
        style={{ marginTop: '30px' }}
      >
        🔄 Reset Game
      </button>
    </div>
  );
};

// 2. Mirror Match Game Component
const MirrorMatchGame = () => {
  const [currentPair, setCurrentPair] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [feedback, setFeedback] = useState('');

  const pairs = useMemo(() => [
    { left: 'b', right: 'b', isMirror: false },
    { left: 'b', right: 'd', isMirror: true },
    { left: 'd', right: 'd', isMirror: false },
    { left: 'd', right: 'b', isMirror: true },
    { left: 'p', right: 'p', isMirror: false },
    { left: 'p', right: 'q', isMirror: true },
    { left: 'q', right: 'q', isMirror: false },
    { left: 'q', right: 'p', isMirror: true },
    { left: 'b', right: 'p', isMirror: false },
    { left: 'd', right: 'd', isMirror: false }
  ], []); // Wrapped pairs initialization in useMemo

  const nextPair = useCallback(() => {
    const randomPair = pairs[Math.floor(Math.random() * pairs.length)];
    setCurrentPair(randomPair);
    setFeedback('');
  }, [pairs]); // Added 'pairs' to dependency array

  useEffect(() => {
    nextPair();
  }, [nextPair]); // Wrapped nextPair in useCallback and reordered

  const handleAnswer = (userAnswer) => {
    setAttempts(a => a + 1);
    if (userAnswer === currentPair.isMirror) {
      setFeedback('✅ Correct! Well done!');
    } else {
      setFeedback('❌ Not quite. Try again!');
    }
    setTimeout(nextPair, 1500);
  };

  if (!currentPair) return null;

  return (
    <div className="quiz-card">
      <h2 className="section-title">🪞 Mirror Match</h2>
      <p className="setup-description">
        Are these letters the same or mirror images of each other?
      </p>

      <div style={{ 
        textAlign: 'center', 
        fontSize: '1.8rem', 
        fontWeight: 'bold', 
        color: '#667eea',
        margin: '20px 0' 
      }}>
        Attempts: {attempts}
      </div>

      <div className="mirror-compare">
        <div className="mirror-letter mirror-letter-left">
          {currentPair.left}
        </div>
        <div className="mirror-vs">vs</div>
        <div className="mirror-letter mirror-letter-right">
          {currentPair.right}
        </div>
      </div>

      {feedback && (
        <div className={`feedback ${feedback.includes('✅') ? 'correct' : 'incorrect'}`}>
          {feedback}
        </div>
      )}

      <div className="mirror-btns">
        <button
          className="activity-btn mirror-btn-same"
          onClick={() => handleAnswer(false)}
        >
          Same
        </button>
        <button
          className="activity-btn mirror-btn-mirror"
          onClick={() => handleAnswer(true)}
        >
          Mirror
        </button>
      </div>

      <button
        className="start-btn"
        onClick={() => { setAttempts(0); setFeedback(''); nextPair(); }}
        style={{ marginTop: '30px' }}
      >
        🔄 Reset Game
      </button>
    </div>
  );
};

// 3. Trace and Flip Game Component
const TraceFlipGame = () => {
  const [selectedLetter, setSelectedLetter] = useState('b');
  const [isFlipped, setIsFlipped] = useState(false);
  const [tracePath, setTracePath] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);

  const letters = ['b', 'd', 'p', 'q'];
  
  const getFlippedLetter = (letter) => {
    const flipMap = { 'b': 'd', 'd': 'b', 'p': 'q', 'q': 'p' };
    return flipMap[letter];
  };

  const handleMouseDown = (e) => {
    setIsDrawing(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setTracePath([{ x: e.clientX - rect.left, y: e.clientY - rect.top }]);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTracePath(prev => [...prev, { x: e.clientX - rect.left, y: e.clientY - rect.top }]);
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const touch = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();
    setTracePath([{ x: touch.clientX - rect.left, y: touch.clientY - rect.top }]);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const touch = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();
    setTracePath(prev => [...prev, { x: touch.clientX - rect.left, y: touch.clientY - rect.top }]);
  };

  const handleTouchEnd = () => {
    setIsDrawing(false);
  };

  const clearTrace = () => {
    setTracePath([]);
  };

  return (
    <div className="quiz-card">
      <h2 className="section-title">✍️ Trace and Flip</h2>
      <p className="setup-description">
        Trace the letter with your mouse, then flip it to see how it changes!
      </p>

      <div style={{ marginBottom: '30px' }}>
        <label style={{ fontSize: '1.6rem', fontWeight: 'bold', marginRight: '15px' }}>
          Choose a letter:
        </label>
        {letters.map(letter => (
          <button
            key={letter}
            className={selectedLetter === letter ? 'check-btn' : 'help-btn'}
            onClick={() => {
              setSelectedLetter(letter);
              setIsFlipped(false);
              clearTrace();
            }}
            style={{ 
              margin: '0 5px',
              fontSize: '2rem',
              padding: '10px 25px'
            }}
          >
            {letter}
          </button>
        ))}
      </div>

      <div
        className="trace-canvas"
        style={{
          background: isFlipped ? '#FCE4EC' : '#E3F2FD',
          border: '4px solid ' + (isFlipped ? '#E91E63' : '#2196F3'),
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div style={{
          fontSize: '15rem',
          fontWeight: 'bold',
          color: isFlipped ? '#C2185B' : '#1976D2',
          opacity: 0.3,
          userSelect: 'none'
        }}>
          {isFlipped ? getFlippedLetter(selectedLetter) : selectedLetter}
        </div>

        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none'
          }}
        >
          {tracePath.length > 1 && (
            <polyline
              points={tracePath.map(p => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke="#FF5722"
              strokeWidth="8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </svg>
      </div>

      <div style={{ 
        display: 'flex', 
        gap: '15px', 
        justifyContent: 'center' 
      }}>
        <button
          className="activity-btn"
          onClick={() => setIsFlipped(!isFlipped)}
          style={{ fontSize: '1.5rem', padding: '15px 35px' }}
        >
          {isFlipped ? '↩️ Flip Back' : '🔄 Flip Letter'}
        </button>
        <button
          className="help-btn"
          onClick={clearTrace}
          style={{ fontSize: '1.5rem', padding: '15px 35px' }}
        >
          🗑️ Clear Trace
        </button>
      </div>
    </div>
  );
};

// 4. Word Completion Game Component
const WordCompletionGame = () => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [selectedLetter, setSelectedLetter] = useState('');
  const [feedback, setFeedback] = useState('');
  const [score, setScore] = useState(0);

  const words = [
    { word: 'ball', missing: 'b', options: ['b', 'd', 'p', 'q'] },
    { word: 'dog', missing: 'd', options: ['b', 'd', 'p', 'q'] },
    { word: 'pen', missing: 'p', options: ['b', 'd', 'p', 'q'] },
    { word: 'queen', missing: 'q', options: ['b', 'd', 'p', 'q'] },
    { word: 'bed', missing: 'b', options: ['b', 'd', 'p', 'q'] },
    { word: 'duck', missing: 'd', options: ['b', 'd', 'p', 'q'] },
    { word: 'pig', missing: 'p', options: ['b', 'd', 'p', 'q'] },
    { word: 'quiet', missing: 'q', options: ['b', 'd', 'p', 'q'] }
  ];

  const currentWord = words[currentWordIndex];
  const displayWord = currentWord.word.replace(currentWord.missing, '_');

  const handleLetterSelect = (letter) => {
    setSelectedLetter(letter);
    if (letter === currentWord.missing) {
      setScore(s => s + 1);
      setFeedback('✅ Correct! The word is: ' + currentWord.word);
      setTimeout(() => {
        if (currentWordIndex < words.length - 1) {
          setCurrentWordIndex(i => i + 1);
          setSelectedLetter('');
          setFeedback('');
        } else {
          setFeedback(`🎉 Game Complete! Final Score: ${score + 1}/${words.length}`);
        }
      }, 2000);
    } else {
      setFeedback('❌ Try again!');
      setTimeout(() => setFeedback(''), 1500);
    }
  };

  const resetGame = () => {
    setCurrentWordIndex(0);
    setSelectedLetter('');
    setFeedback('');
    setScore(0);
  };

  return (
    <div className="quiz-card">
      <h2 className="section-title">📝 Word Completion</h2>
      <p className="setup-description">
        Choose the correct letter to complete the word!
      </p>

      <div style={{ 
        textAlign: 'center', 
        fontSize: '1.8rem', 
        fontWeight: 'bold', 
        color: '#667eea',
        margin: '20px 0' 
      }}>
        Score: {score} / {words.length}
      </div>

      <div className="word-display">
        {displayWord.split('').map((char, idx) => (
          <span key={idx} className={`word-char ${char === '_' ? 'word-char-blank' : ''}`}>
            {char === '_' && selectedLetter ? selectedLetter : char}
          </span>
        ))}
      </div>

      {feedback && (
        <div className={`feedback ${feedback.includes('✅') ? 'correct' : 'incorrect'}`}>
          {feedback}
        </div>
      )}

      <div className="game-letter-options">
        {currentWord.options.map(letter => (
          <button
            key={letter}
            className="letter-tile game-letter-tile"
            onClick={() => handleLetterSelect(letter)}
          >
            {letter}
          </button>
        ))}
      </div>

      {currentWordIndex === words.length - 1 && feedback.includes('🎉') && (
        <button 
          className="start-btn" 
          onClick={resetGame}
          style={{ marginTop: '30px' }}
        >
          🔄 Play Again
        </button>
      )}

      {!(currentWordIndex === words.length - 1 && feedback.includes('🎉')) && (
        <button
          className="start-btn"
          onClick={resetGame}
          style={{ marginTop: '30px' }}
        >
          🔄 Reset Game
        </button>
      )}
    </div>
  );
};

// 5. Picture Association Game Component
const PictureAssociationGame = () => {
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  const allAssociations = [
    { letter: 'b', image: '⚽', word: 'ball' },
    { letter: 'd', image: '🐕', word: 'dog' },
    { letter: 'p', image: '🖊️', word: 'pen' },
    { letter: 'q', image: '👑', word: 'queen' },
    { letter: 'b', image: '🐝', word: 'bee' },
    { letter: 'd', image: '🚪', word: 'door' },
    { letter: 'p', image: '🍕', word: 'pizza' },
    { letter: 'q', image: '❓', word: 'question' },
    { letter: 'b', image: '📚', word: 'book' },
    { letter: 'd', image: '🥁', word: 'drum' },
    { letter: 'p', image: '🐷', word: 'pig' },
    { letter: 'q', image: '🦆', word: 'quack' },
    { letter: 'b', image: '🦋', word: 'butterfly' },
    { letter: 'd', image: '💎', word: 'diamond' },
    { letter: 'p', image: '🍑', word: 'peach' },
    { letter: 'q', image: '🪶', word: 'quill' },
    { letter: 'b', image: '🎈', word: 'balloon' },
    { letter: 'd', image: '🦌', word: 'deer' },
    { letter: 'p', image: '🐧', word: 'penguin' },
    { letter: 'q', image: '🏰', word: 'quest' },
    { letter: 'b', image: '🍌', word: 'banana' },
    { letter: 'd', image: '🎲', word: 'dice' },
    { letter: 'p', image: '🌸', word: 'petal' },
    { letter: 'q', image: '🤫', word: 'quiet' },
    { letter: 'b', image: '🦁', word: 'bear' },
    { letter: 'd', image: '🐬', word: 'dolphin' },
    { letter: 'p', image: '🦜', word: 'parrot' },
    { letter: 'q', image: '🍈', word: 'quince' },
    { letter: 'b', image: '🚌', word: 'bus' },
    { letter: 'd', image: '🎯', word: 'dart' },
    { letter: 'p', image: '🪴', word: 'plant' },
    { letter: 'q', image: '🧩', word: 'quiz' },
    { letter: 'b', image: '🧁', word: 'bun' },
    { letter: 'd', image: '🦟', word: 'dragonfly' },
    { letter: 'p', image: '🥞', word: 'pancake' },
    { letter: 'q', image: '🔲', word: 'quad' },
    { letter: 'b', image: '🎀', word: 'bow' },
    { letter: 'd', image: '🍩', word: 'doughnut' },
    { letter: 'p', image: '🦚', word: 'peacock' },
    { letter: 'q', image: '💜', word: 'queen bee' },
    { letter: 'b', image: '🛶', word: 'boat' },
    { letter: 'd', image: '🦊', word: 'den' },
    { letter: 'p', image: '🌊', word: 'puddle' },
    { letter: 'q', image: '🪙', word: 'quarter' },
    { letter: 'b', image: '🌙', word: 'bright' },
    { letter: 'd', image: '💧', word: 'drop' },
    { letter: 'p', image: '🎠', word: 'pony' },
    { letter: 'q', image: '🏹', word: 'quiver' },
    { letter: 'b', image: '🧸', word: 'bear toy' },
    { letter: 'd', image: '🌊', word: 'deep' },
  ];

  const shuffle = (arr) => {
    const shuffled = [...arr];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const [associations, setAssociations] = useState(() => shuffle(allAssociations));

  const currentItem = associations[currentIndex];
  const options = ['b', 'd', 'p', 'q'];

  const handleDrop = (letter) => {
    if (letter === currentItem.letter) {
      setScore(s => s + 1);
      setFeedback(`✅ Correct! ${currentItem.image} is for "${currentItem.word}"`);
      setTimeout(() => {
        if (currentIndex < associations.length - 1) {
          setCurrentIndex(i => i + 1);
          setFeedback('');
        } else {
          setFeedback(`🎉 Complete! Score: ${score + 1}/${associations.length}`);
        }
      }, 2000);
    } else {
      setFeedback('❌ Try again!');
      setTimeout(() => setFeedback(''), 1500);
    }
  };

  const resetGame = () => {
    setAssociations(shuffle(allAssociations));
    setCurrentIndex(0);
    setScore(0);
    setFeedback('');
  };

  return (
    <div className="quiz-card">
      <h2 className="section-title">🖼️ Picture Association</h2>
      <p className="setup-description">
        Click the letter that matches the picture!
      </p>

      <div style={{ 
        textAlign: 'center', 
        fontSize: '1.8rem', 
        fontWeight: 'bold', 
        color: '#667eea',
        margin: '20px 0' 
      }}>
        Score: {score} / {associations.length}
      </div>

      <div className="picture-assoc">
        <div className="picture-assoc-image">
          {currentItem.image}
        </div>

      </div>

      {feedback && (
        <div className={`feedback ${feedback.includes('✅') ? 'correct' : 'incorrect'}`}>
          {feedback}
        </div>
      )}

      <div className="game-letter-options">
        {options.map(letter => (
          <button
            key={letter}
            className="letter-tile game-letter-tile"
            onClick={() => handleDrop(letter)}
          >
            {letter}
          </button>
        ))}
      </div>

      {currentIndex === associations.length - 1 && feedback.includes('🎉') && (
        <button 
          className="start-btn" 
          onClick={resetGame}
          style={{ marginTop: '30px' }}
        >
          🔄 Play Again
        </button>
      )}

      {!(currentIndex === associations.length - 1 && feedback.includes('🎉')) && (
        <button
          className="start-btn"
          onClick={resetGame}
          style={{ marginTop: '30px' }}
        >
          🔄 Reset Game
        </button>
      )}
    </div>
  );
};

// 6. Spot the Difference Game Component
const SpotDifferenceGame = () => {
  const [grid, setGrid] = useState([]);
  const [found, setFound] = useState([]);
  const [targetLetter, setTargetLetter] = useState('b');

  const generateGrid = useCallback(() => {
    const letters = ['b', 'd', 'p', 'q'];
    const otherLetters = letters.filter(l => l !== targetLetter);
    const newGrid = [];
    
    // Create a 6x6 grid
    for (let i = 0; i < 36; i++) {
      if (Math.random() < 0.3) { // 30% chance of target letter
        newGrid.push({ id: i, letter: targetLetter, isTarget: true });
      } else {
        const randomLetter = otherLetters[Math.floor(Math.random() * otherLetters.length)];
        newGrid.push({ id: i, letter: randomLetter, isTarget: false });
      }
    }
    setGrid(newGrid);
    setFound([]);
  }, [targetLetter]);

  useEffect(() => {
    generateGrid();
  }, [generateGrid]); // Wrapped generateGrid in useCallback and reordered

  const handleLetterClick = (item) => {
    if (found.includes(item.id)) return;

    if (item.isTarget) {
      setFound(prev => [...prev, item.id]);
    }
  };

  const changeTarget = (letter) => {
    setTargetLetter(letter);
  };

  const targetCount = grid.filter(item => item.isTarget).length;

  return (
    <div className="quiz-card">
      <h2 className="section-title">🔍 Spot the Difference</h2>
      <p className="setup-description">
        Find all the "{targetLetter}" letters in the grid!
      </p>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ fontSize: '1.6rem', fontWeight: 'bold', marginRight: '15px' }}>
          Find letter:
        </label>
        {['b', 'd', 'p', 'q'].map(letter => (
          <button
            key={letter}
            className={targetLetter === letter ? 'check-btn' : 'help-btn'}
            onClick={() => changeTarget(letter)}
            style={{ 
              margin: '0 5px',
              fontSize: '2rem',
              padding: '10px 25px'
            }}
          >
            {letter}
          </button>
        ))}
      </div>

      <div style={{ 
        textAlign: 'center', 
        fontSize: '1.8rem', 
        fontWeight: 'bold', 
        color: '#667eea',
        margin: '20px 0' 
      }}>
        Found: {found.length} / {targetCount}
      </div>

      <div className="spot-diff-grid">
        {grid.map(item => (
          <div
            key={item.id}
            onClick={() => handleLetterClick(item)}
            className={`spot-diff-item ${found.includes(item.id) ? 'spot-diff-item-found' : ''}`}
          >
            {item.letter}
          </div>
        ))}
      </div>

      {found.length === targetCount && targetCount > 0 && (
        <div className="feedback correct">
          🎉 You found all {targetCount} "{targetLetter}" letters!
        </div>
      )}

      <button 
        className="start-btn" 
        onClick={generateGrid}
        style={{ marginTop: '30px' }}
      >
        🔄 New Grid
      </button>
    </div>
  );
};


export default PhonemeGames;
